<template>
  <div class="phone-input-wrapper">
    <div class="flag-selector" @click="toggleCountryList">
      <span v-if="selectedCountry">{{ getCountryFlag(selectedCountry) }}</span>
      <div v-if="showCountryList" class="country-list" @click.stop>
        <div class="country-search">
          <input
            v-model="countrySearch"
            placeholder="Search country..."
            class="country-search-input"
            @click.stop
            ref="countrySearchInput"
          >
        </div>
        <div class="country-options">
          <div
            v-for="country in filteredCountries"
            :key="country.code"
            class="country-option"
            :class="{ 'selected': selectedCountry === country.code }"
            @click="selectCountry(country.code)"
          >
            <span class="country-flag">{{ getCountryFlag(country.code) }}</span>
            <span class="country-name">{{ country.name }}</span>
            <span class="country-dial">{{ country.dialCode }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Plain input that base-input can style -->
    <input :style="inputStyle"
           ref="phoneInput"
           type="tel"
           :value="displayValue"
           :placeholder="currentPlaceholder"
           @input="handleInput"
           @focus="handleFocus"
           @blur="handleBlur"
           @keydown="handleKeydown"
           class="form-control phone-number-input"
           v-bind="$attrs"
    />
  </div>
</template>

<script>
import {
  AsYouType,
  parsePhoneNumber,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/examples.mobile.json'
import { diacriticIncludes } from '@/util/search'
import { parseStoredPhone } from '@/util/phoneFormatter'

const countryNames = {
  'US': 'United States',
  'CA': 'Canada',
  'MX': 'Mexico',
  'PA': 'Panama',
  'GB': 'United Kingdom',
  'FR': 'France',
  'DE': 'Germany',
  'IT': 'Italy',
  'ES': 'Spain',
  'PT': 'Portugal',
  'NL': 'Netherlands',
  'BE': 'Belgium',
  'CH': 'Switzerland',
  'AT': 'Austria',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'PL': 'Poland',
  'GR': 'Greece',
  'IE': 'Ireland',
  'AU': 'Australia',
  'NZ': 'New Zealand',
  'JP': 'Japan',
  'CN': 'China',
  'KR': 'South Korea',
  'IN': 'India',
  'SG': 'Singapore',
  'MY': 'Malaysia',
  'TH': 'Thailand',
  'ID': 'Indonesia',
  'PH': 'Philippines',
  'VN': 'Vietnam',
  'HK': 'Hong Kong',
  'TW': 'Taiwan',
  'BR': 'Brazil',
  'AR': 'Argentina',
  'CL': 'Chile',
  'CO': 'Colombia',
  'PE': 'Peru',
  'VE': 'Venezuela',
  'AE': 'United Arab Emirates',
  'SA': 'Saudi Arabia',
  'IL': 'Israel',
  'TR': 'Turkey',
  'ZA': 'South Africa',
  'EG': 'Egypt',
  'NG': 'Nigeria',
  'KE': 'Kenya',
  'RU': 'Russia',
  'UA': 'Ukraine',
  'CZ': 'Czech Republic',
  'RO': 'Romania',
  'HU': 'Hungary',
  'LU': 'Luxembourg',
  'IS': 'Iceland',
  'HR': 'Croatia',
  'BG': 'Bulgaria',
  'SK': 'Slovakia',
  'SI': 'Slovenia',
  'EE': 'Estonia',
  'LV': 'Latvia',
  'LT': 'Lithuania',
  'MT': 'Malta',
  'CY': 'Cyprus'
};

const countryFlags = {
  'US': '🇺🇸', 'CA': '🇨🇦', 'MX': '🇲🇽', 'PA': '🇵🇦',
  'GB': '🇬🇧', 'FR': '🇫🇷', 'DE': '🇩🇪', 'IT': '🇮🇹',
  'ES': '🇪🇸', 'PT': '🇵🇹', 'NL': '🇳🇱', 'BE': '🇧🇪',
  'CH': '🇨🇭', 'AT': '🇦🇹', 'SE': '🇸🇪', 'NO': '🇳🇴',
  'DK': '🇩🇰', 'FI': '🇫🇮', 'PL': '🇵🇱', 'GR': '🇬🇷',
  'IE': '🇮🇪', 'AU': '🇦🇺', 'NZ': '🇳🇿', 'JP': '🇯🇵',
  'CN': '🇨🇳', 'KR': '🇰🇷', 'IN': '🇮🇳', 'SG': '🇸🇬',
  'MY': '🇲🇾', 'TH': '🇹🇭', 'ID': '🇮🇩', 'PH': '🇵🇭',
  'VN': '🇻🇳', 'HK': '🇭🇰', 'TW': '🇹🇼', 'BR': '🇧🇷',
  'AR': '🇦🇷', 'CL': '🇨🇱', 'CO': '🇨🇴', 'PE': '🇵🇪',
  'VE': '🇻🇪', 'AE': '🇦🇪', 'SA': '🇸🇦', 'IL': '🇮🇱',
  'TR': '🇹🇷', 'ZA': '🇿🇦', 'EG': '🇪🇬', 'NG': '🇳🇬',
  'KE': '🇰🇪', 'RU': '🇷🇺', 'UA': '🇺🇦', 'CZ': '🇨🇿',
  'RO': '🇷🇴', 'HU': '🇭🇺', 'LU': '🇱🇺', 'IS': '🇮🇸',
  'HR': '🇭🇷', 'BG': '🇧🇬', 'SK': '🇸🇰', 'SI': '🇸🇮',
  'EE': '🇪🇪', 'LV': '🇱🇻', 'LT': '🇱🇹', 'MT': '🇲🇹',
  'CY': '🇨🇾'
};

export default {
  name: 'PhoneNumberInput',
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: ''
    },
    defaultCountry: {
      type: String,
      default: null
    },
    inputStyle: {
      type: Object
    }
  },
  data() {
    return {
      displayValue: '',
      selectedCountry: null,
      showCountryList: false,
      countrySearch: '',
      formatter: null,
      rawValue: '',
      allCountries: [],
      // Country read out of a stored E.164 value; wins over defaultCountry
      // until the user picks a flag themselves.
      valueCountry: null
    };
  },
  computed: {
    filteredCountries() {
      if (!this.countrySearch) return this.allCountries;
      const search = this.countrySearch.toLowerCase();
      return this.allCountries.filter(c =>
        diacriticIncludes(c.name, search) ||
        c.dialCode.includes(search) ||
        diacriticIncludes(c.code, search)
      );
    },
    currentPlaceholder() {
      if (!this.selectedCountry) return '123 456 7890';
      try {
        const example = getExampleNumber(this.selectedCountry, examples);
        if (example) {
          // Format the example number for display
          const formatted = example.formatNational();
          // Remove any x characters from the placeholder
          if (formatted.includes('x')) {
            // Use predefined friendly placeholders for common countries
            const friendlyPlaceholders = {
              'GB': '20 7123 4567',
              'US': '(201) 555-5555',
              'CA': '(506) 555-5555',
              'AU': '0412 345 678',
              'FR': '06 12 34 56 78',
              'DE': '0151 12345678',
              'IT': '312 345 6789',
              'ES': '612 34 56 78',
              'NL': '06 12345678',
              'JP': '090 1234 5678',
              'CN': '131 2345 6789',
              'IN': '98765 43210',
              'BR': '(11) 98765-4321',
              'MX': '55 1234 5678',
              'RU': '912 345-67-89',
              'KR': '010-1234-5678'
            };
            return friendlyPlaceholders[this.selectedCountry] || formatted.replace(/x/gi, '5');
          }
          return formatted;
        }
      } catch (e) {
        console.warn('Could not get example number for', this.selectedCountry);
      }

      // Fallback placeholders for common countries
      const defaultPlaceholders = {
        'GB': '20 7123 4567',
        'US': '(201) 555-5555',
        'CA': '(506) 555-5555',
        'AU': '0412 345 678',
        'FR': '06 12 34 56 78',
        'DE': '0151 12345678',
        'IT': '312 345 6789',
        'ES': '612 34 56 78',
        'NL': '06 12345678',
        'JP': '090 1234 5678',
        'CN': '131 2345 6789',
        'IN': '98765 43210',
        'BR': '(11) 98765-4321',
        'MX': '55 1234 5678',
        'RU': '912 345-67-89',
        'KR': '010-1234-5678',
        'SG': '8123 4567',
        'HK': '5123 4567',
        'NZ': '021 123 4567',
        'IE': '085 123 4567',
        'ZA': '071 123 4567',
        'AE': '50 123 4567',
        'SA': '51 234 5678',
        'IL': '050 123 4567',
        'SE': '070 123 45 67',
        'NO': '406 12 345',
        'DK': '20 12 34 56',
        'FI': '040 123 4567',
        'PL': '512 345 678',
        'PT': '912 345 678',
        'CH': '078 123 45 67',
        'AT': '0664 123456',
        'BE': '0470 12 34 56',
        'CZ': '601 123 456',
        'HU': '06 20 123 4567',
        'GR': '691 234 5678',
        'TR': '0532 123 4567',
        'TH': '081 234 5678',
        'VN': '091 234 56 78',
        'PH': '0917 123 4567',
        'ID': '0812-1234-5678',
        'MY': '012-345 6789',
        'TW': '0912 345 678',
        'AR': '011 15-2345-6789',
        'CL': '9 8765 4321',
        'CO': '321 123 4567',
        'PE': '987 654 321',
        'VE': '0412 123 4567',
        'EG': '0100 123 4567',
        'NG': '0802 123 4567',
        'KE': '0712 123456',
        'UA': '067 123 4567',
        'RO': '0712 345 678',
        'PA': '6123-4567'
      };

      return defaultPlaceholders[this.selectedCountry] || '123 456 7890';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal === this.rawValue) return;

        // A stored E.164 value carries its own country ("+32..." is Belgian).
        // Open under that flag and keep the national digits as the raw value,
        // so the field matches the number and edits re-parse under it. Without
        // this a Belgian number loads under the venue's US flag and a single
        // edited digit saves it back as an invalid US number (OTC-3141).
        const stored = parseStoredPhone(newVal);
        if (stored && isSupportedCountry(stored.country)) {
          this.valueCountry = stored.country;
          this.selectedCountry = stored.country;
          this.rawValue = stored.nationalNumber;
        } else {
          this.valueCountry = null;
          this.rawValue = newVal;
        }
        this.formatCurrentValue();
      }
    },
    defaultCountry: {
      immediate: true,
      handler(code) {
        if (this.valueCountry) {
          // The loaded number already named its country; the venue default
          // only applies to an empty or country-less value.
        } else if (code && isSupportedCountry(code)) {
          this.selectedCountry = code;
        } else if (!this.selectedCountry) {
          this.selectedCountry = this.detectCountryFromLocale();
        }
        this.initializeFormatter();
        this.formatCurrentValue();
      }
    },
    selectedCountry() {
      this.initializeFormatter();
      this.formatCurrentValue();
    }
  },
  mounted() {
    this.initializeCountries();

    // Initialize country based on prop or browser locale
    if (!this.selectedCountry) {
      if (this.defaultCountry && isSupportedCountry(this.defaultCountry)) {
        this.selectedCountry = this.defaultCountry;
      } else {
        this.selectedCountry = this.detectCountryFromLocale();
      }
    }

    this.initializeFormatter();
    this.formatCurrentValue();

    document.addEventListener('click', this.closeCountryList);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeCountryList);
  },
  methods: {
    initializeCountries() {
      // Get all supported countries and build the list
      const countries = getCountries();
      this.allCountries = countries
        .filter(code => countryNames[code]) // Only include countries we have names for
        .map(code => {
          try {
            const callingCode = getCountryCallingCode(code);
            return {
              code,
              name: countryNames[code] || code,
              dialCode: `+${callingCode}`
            };
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    getCountryFlag(code) {
      return countryFlags[code] || '🏳️';
    },

    detectCountryFromLocale() {
      const browserLocale = navigator.language || navigator.userLanguage || 'en-US';
      const localeParts = browserLocale.split('-');
      const countryCode = localeParts.length > 1 ? localeParts[1].toUpperCase() : null;

      if (countryCode && isSupportedCountry(countryCode)) {
        return countryCode;
      }

      // Fallback logic for common language-to-country mappings
      const langCode = localeParts[0].toLowerCase();
      const langToCountry = {
        'en': 'US',
        'es': 'ES',
        'fr': 'FR',
        'de': 'DE',
        'it': 'IT',
        'pt': 'PT',
        'zh': 'CN',
        'ja': 'JP',
        'ko': 'KR',
        'ru': 'RU',
        'ar': 'SA'
      };

      return langToCountry[langCode] || 'US';
    },

    initializeFormatter() {
      try {
        this.formatter = new AsYouType(this.selectedCountry);
      } catch (e) {
        console.warn('Failed to initialize phone formatter:', e);
        this.formatter = null;
      }
    },

    formatCurrentValue() {
      if (!this.rawValue) {
        this.displayValue = '';
        return;
      }

      // Ensure formatter is initialized
      if (!this.formatter) {
        this.initializeFormatter();
      }

      // Try to parse the existing value
      try {
        const parsed = parsePhoneNumber(this.rawValue, this.selectedCountry);
        if (parsed) {
          // If it's a valid number, format it
          this.displayValue = parsed.formatNational();
          return;
        }
      } catch (e) {
        // Not a valid complete number, use AsYouType formatter
      }

      // Safe check before using formatter
      if (this.formatter && typeof this.formatter.reset === 'function') {
        this.formatter.reset();
        this.displayValue = this.formatter.input(this.rawValue);
      } else {
        // Fallback if formatter isn't available
        this.displayValue = this.rawValue;
      }
    },

    handleInput(event) {
      const input = event.target;
      const inputValue = input.value;
      const cursorPos = input.selectionStart;

      // Reset formatter for new input
      this.formatter.reset();

      // Input through formatter
      const formatted = this.formatter.input(inputValue);
      this.displayValue = formatted;

      // Get the national number (digits only, no formatting)
      const nationalNumber = this.formatter.getNationalNumber() || '';
      this.rawValue = nationalNumber;

      // Emit the raw digits
      this.$emit('input', nationalNumber);
      this.emitPhoneData();

      // Try to restore cursor position intelligently
      this.$nextTick(() => {
        // If we're at the end, stay at the end
        if (cursorPos >= inputValue.length) {
          input.setSelectionRange(formatted.length, formatted.length);
        } else {
          // Try to maintain relative position
          const ratio = cursorPos / inputValue.length;
          const newPos = Math.round(formatted.length * ratio);
          input.setSelectionRange(newPos, newPos);
        }
      });
    },
    emitPhoneData() {
      const phoneData = this.getPhoneData();
      this.$emit('phone-update', phoneData);  // <-- HERE is where phone-update is defined
    },
    getPhoneData() {
      if (!this.rawValue) {
        return {
          isValid: false,
          raw: '',
          national: '',
          international: '',
          e164: '',
          countryCode: this.selectedCountry,
          dialCode: this.selectedCountry ? `+${getCountryCallingCode(this.selectedCountry)}` : ''
        };
      }

      try {
        const parsed = parsePhoneNumber(this.rawValue, this.selectedCountry);
        if (parsed) {
          return {
            isValid: parsed.isValid(),
            raw: this.rawValue,                           // "4159405915"
            national: parsed.formatNational(),             // "(415) 940-5915"
            international: parsed.formatInternational(),   // "+1 415 940 5915"
            e164: parsed.format('E.164'),                 // "+14159405915"
            countryCode: this.selectedCountry,
            dialCode: `+${parsed.countryCallingCode}`
          };
        }
      } catch (e) {
        // Not a complete valid number yet
      }

      // Return partial data for incomplete numbers
      return {
        isValid: false,
        raw: this.rawValue,
        national: this.displayValue,
        international: '',
        e164: '',
        countryCode: this.selectedCountry,
        dialCode: this.selectedCountry ? `+${getCountryCallingCode(this.selectedCountry)}` : ''
      };
    },
    handleKeydown(event) {
      // Allow paste shortcuts (Ctrl+V or Cmd+V)
      if ((event.ctrlKey || event.metaKey) && event.keyCode === 86) {
        return;
      }

      // Allow: backspace, delete, tab, escape, enter, ctrl+A/C/V/X
      const allowedKeys = [8, 9, 27, 13, 46];
      const isCtrlKey = (event.ctrlKey || event.metaKey) && [65, 67, 86, 88].includes(event.keyCode);

      if (allowedKeys.includes(event.keyCode) || isCtrlKey) {
        return;
      }

      // Allow function keys
      if (event.keyCode >= 112 && event.keyCode <= 123) {
        return;
      }

      // Allow home, end, left, right
      if (event.keyCode >= 35 && event.keyCode <= 39) {
        return;
      }

      // Prevent if not a number key
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) &&
        (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    },

    toggleCountryList() {
      // A disabled input (attribute forwarded to the inner <input>) keeps its country too.
      if (this.$attrs.disabled) return;
      this.showCountryList = !this.showCountryList;
      if (this.showCountryList) {
        this.$nextTick(() => {
          if (this.$refs.countrySearchInput) {
            this.$refs.countrySearchInput.focus();
          }
        });
      }
    },

    selectCountry(countryCode) {
      this.selectedCountry = countryCode;
      this.valueCountry = null;
      this.showCountryList = false;
      this.countrySearch = '';

      // Clear the input when country changes
      this.displayValue = '';
      this.rawValue = '';
      this.$emit('input', '');

      // Emit country change event
      this.$emit('country-change', {
        code: countryCode,
        name: countryNames[countryCode],
        dialCode: `+${getCountryCallingCode(countryCode)}`
      });

      // Focus back on input
      this.$nextTick(() => {
        this.$refs.phoneInput.focus();
      });
    },

    closeCountryList(e) {
      if (!this.$el.contains(e.target)) {
        this.showCountryList = false;
      }
    },

    handleFocus() {
      this.$emit('focus');
    },

    handleBlur() {
      this.showCountryList = false;
      this.$emit('blur');
    },

    // Public method to validate the current number
    isValid() {
      if (!this.rawValue) return false;
      try {
        const parsed = parsePhoneNumber(this.rawValue, this.selectedCountry);
        return parsed && parsed.isValid();
      } catch (e) {
        return false;
      }
    },

    // Public method to get the formatted international number
    getInternationalNumber() {
      if (!this.rawValue) return '';
      try {
        const parsed = parsePhoneNumber(this.rawValue, this.selectedCountry);
        return parsed ? parsed.formatInternational() : '';
      } catch (e) {
        return '';
      }
    }
  }
};
</script>

<style scoped>
/* Wrapper to contain the input and flag */
.phone-input-wrapper {
  position: relative;
  width: 100%;
}

/* Input with padding for flag */
.phone-number-input {
  padding-left: 50px !important;
  width: 100%;
}

/* Position flag inside the input */
.flag-selector {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  cursor: pointer;
  height: 30px;
  display: flex;
  align-items: center;
  font-size: 20px;
}

/* Dropdown styles remain the same */
.country-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  width: 280px;
  overflow: hidden;
  z-index: 1000;
}

.country-search {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.country-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.country-options {
  max-height: 250px;
  overflow-y: auto;
}

.country-option {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
}

.country-option:hover {
  background-color: #f5f5f5;
}

.country-option.selected {
  background-color: #e3f2fd;
}

.country-flag {
  font-size: 20px;
  margin-right: 10px;
}

.country-name {
  flex: 1;
  font-size: 14px;
}

.country-dial {
  font-size: 13px;
  color: #666;
  margin-left: 8px;
}
</style>
